/**
 * CurrentUserProvider — the signed-in user's `users/{uid}` document, once.
 *
 * Four places listened to this doc, and on a gym page two of them ran at the
 * same time: the app root (for the consent gate), `useGymData`, `GymSelector`
 * and `GlobalProfile`. Same document, four subscriptions, and — the part that
 * mattered — three different sets of guards.
 *
 * The guards are the reason this is a correctness change and not tidying.
 * Firestore here is memory-only (see platform-invariants) and the PWA falls
 * back to long polling, so a listener can fire a transient EMPTY cache snapshot
 * before the server round-trip lands. Only the root's copy skipped it. Acting
 * on that emission makes a returning user look like a brand-new one for a
 * second, which is why the root grew:
 *
 * - **`includeMetadataChanges` + skip the empty cache snapshot.** Without it a
 *   returning user flashes the blocking terms modal. The metadata option is
 *   what still lets a genuinely absent doc (a real new user) resolve, so the
 *   modal does appear for them.
 * - **A doc that existed and then vanished is an account deletion, not a first
 *   login.** The delete cascade removes `users/{uid}` well before it finishes,
 *   and the client only signs out at the end — long enough for "no doc" to read
 *   as "no consent yet" and put the terms modal in front of someone on their
 *   way out. Worse, accepting it would write the doc back into existence. So we
 *   freeze on the last known data; the sign-out is already coming.
 *
 * Mounted in `index.js`, above `<App/>`, so the consent gate reads it like
 * everything else rather than owning a private copy.
 *
 * @see src/hooks/useGymData.js — the gym-scoped data, which mirrors this
 *   doc into its `users` list so denormalized names stay fresh.
 */
import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { doc, onSnapshot } from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { auth, db } from "../firebase";
import { localStore } from "../utils/safeStorage";

const CurrentUserContext = createContext(null);

export function CurrentUserProvider({ children }) {
  // Initialize from auth.currentUser so the doc listener starts immediately for
  // a returning user (no gap between auth resolving and the data arriving).
  const [authUser, setAuthUser] = useState(() => auth.currentUser);
  const [userData, setUserData] = useState(null);
  // "The user-doc question has been answered" — either the doc arrived, or the
  // server confirmed there isn't one. NOT the same as `userData !== null`,
  // which cannot tell "still loading" from "genuinely new user".
  const [userDocLoaded, setUserDocLoaded] = useState(false);

  useEffect(() => {
    let unsubDoc = null;
    let listeningUid = null;
    let docSeen = false;

    const listen = (uid) => {
      if (listeningUid === uid) return; // already on this user
      if (unsubDoc) { unsubDoc(); unsubDoc = null; }
      listeningUid = uid;
      docSeen = false;
      unsubDoc = onSnapshot(doc(db, "users", uid), { includeMetadataChanges: true }, (snap) => {
        if (snap.metadata.fromCache && !snap.exists()) return;
        if (!snap.exists() && docSeen) return;
        docSeen = snap.exists();
        const next = snap.exists() ? { uid: snap.id, ...snap.data() } : null;
        setUserData(next);
        setUserDocLoaded(true);
        // Keep the uid-keyed name cache fresh so the next cold boot can paint
        // the stored (possibly custom) name and the right avatar immediately,
        // instead of flashing the Google name or the provider photo. Read back
        // by `useAuth`'s seed and the two profile headers.
        if (next?.name) {
          localStore.setJSON("sendly-user-name", {
            uid, name: next.name, avatarPreference: next.avatarPreference || null,
          });
        }
      });
    };

    if (auth.currentUser) listen(auth.currentUser.uid);

    const unsubAuth = onAuthStateChanged(auth, (user) => {
      setAuthUser(user);
      if (!user) {
        if (unsubDoc) { unsubDoc(); unsubDoc = null; }
        listeningUid = null;
        setUserData(null);
        setUserDocLoaded(false);
        return;
      }
      listen(user.uid);
    });

    return () => { unsubAuth(); if (unsubDoc) unsubDoc(); };
  }, []);

  const value = useMemo(() => ({ authUser, userData, userDocLoaded }), [authUser, userData, userDocLoaded]);
  return <CurrentUserContext.Provider value={value}>{children}</CurrentUserContext.Provider>;
}

/**
 * @returns {{ authUser: object|null, userData: object|null, userDocLoaded: boolean }}
 *
 * Outside the provider (a component rendered in isolation by a test) this
 * returns the signed-out shape rather than throwing — the same choice
 * `useAppTheme` makes, for the same reason.
 */
export function useCurrentUser() {
  return useContext(CurrentUserContext) || { authUser: null, userData: null, userDocLoaded: false };
}
